import { lazy, Suspense, useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Index from "./pages/Index";

const BlogPage = lazy(() => import("./pages/Blog"));
const BlogPostPage = lazy(() => import("./pages/BlogPost"));
const ServicesPage = lazy(() => import("./pages/Services"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const AboutPage = lazy(() => import("./pages/About"));
const ContactPage = lazy(() => import("./pages/Contact"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const CompliancePage = lazy(() => import("./pages/CompliancePage"));
const UshaListingsPage = lazy(() => import("./pages/UshaListings"));
const BlogTagPage = lazy(() => import("./pages/BlogTagPage"));
const CalculatorPage = lazy(() => import("./pages/Calculator"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MobileStickyCta = lazy(() => import("./components/MobileStickyCta"));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const navOffset = 110;
    let cancelled = false;
    const timers: number[] = [];

    const scrollToElement = (element: HTMLElement) => {
      const top = Math.max(0, window.scrollY + element.getBoundingClientRect().top - navOffset);
      window.scrollTo({ top, left: 0, behavior: "auto" });
    };

    const tryScrollToHash = (attempt = 0) => {
      if (cancelled) return;

      const targetId = decodeURIComponent(hash.replace(/^#/, ""));
      const target = targetId ? document.getElementById(targetId) : null;

      if (target) {
        scrollToElement(target);
        return;
      }

      if (attempt >= 60) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        return;
      }

      const timer = window.setTimeout(() => tryScrollToHash(attempt + 1), 50);
      timers.push(timer);
    };

    if (hash) {
      tryScrollToHash();
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [pathname, hash]);

  return null;
};

const App = () => (
  <BrowserRouter>
    <MotionConfig reducedMotion="user">
      <ScrollToTop />
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/usha-listings" element={<UshaListingsPage />} />
          <Route path="/stra-compliance" element={<CompliancePage slug="stra-compliance" />} />
          <Route path="/nsw-requirements" element={<CompliancePage slug="nsw-requirements" />} />
          <Route path="/insurance" element={<CompliancePage slug="insurance" />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/tag/:tag" element={<BlogTagPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/northern-beaches" element={<LocationPage slug="northern-beaches" />} />
          <Route path="/lower-north-shore" element={<LocationPage slug="lower-north-shore" />} />
          <Route path="/bondi" element={<LocationPage slug="bondi" />} />
          <Route path="/manly" element={<LocationPage slug="manly" />} />
          <Route path="/mosman" element={<LocationPage slug="mosman" />} />
          <Route path="/north-shore" element={<LocationPage slug="north-shore" />} />
          <Route path="/sydney-cbd" element={<LocationPage slug="sydney-cbd" />} />
          <Route path="/north-sydney" element={<Navigate to="/sydney-cbd" replace />} />
          <Route path="/inner-sydney" element={<LocationPage slug="inner-sydney" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </MotionConfig>
    <Suspense fallback={null}>
      <MobileStickyCta />
    </Suspense>
  </BrowserRouter>
);

export default App;
